import * as React from 'react'

import { PAGINATION_KIND } from 'modules/core/components/DataTable/constants'
import { PaginationStateContext } from 'modules/core/components/DataTable/contexts/PaginationStateContext'
import useData from 'modules/core/components/DataTable/hooks/useData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

type PaginationAction = 'first' | 'previous' | 'next' | 'last'
type PaginationActions = Record<
  PaginationAction,
  {
    disabled: boolean
    onExecute: () => void
  }
>

// This hook here tries to simplify the returned elements of both pagination contexts, as they
// cointain a lot of information which sometimes is not needed at all
export default function usePagination() {
  const paginationState = React.useContext(PaginationStateContext)
  const { source, queryResult } = useData()

  if (!paginationState) {
    throw new Error('usePagination must be used within a PaginationStateProvider')
  }

  const { pagination, currentPage, setCurrentPage, setVariables } = paginationState

  // Offset logic
  const totalCount = useUnsafeMemo(() => {
    if (pagination.kind !== PAGINATION_KIND.OFFSET || !queryResult.data) {
      return undefined
    }

    const totalCount = source.resolver.dataSource(queryResult.data)?.totalCount

    if (totalCount === undefined) {
      throw new Error('totalCount not present in the response')
    }

    return totalCount as number
  }, [pagination.kind, source.resolver, queryResult.data])

  const totalPages =
    totalCount !== undefined ? Math.ceil(totalCount / pagination.itemsPerPage) : undefined

  const hasPages = currentPage !== undefined && totalPages !== undefined

  const goToPage = (newPage: number) => {
    if (hasPages && newPage <= totalPages && newPage >= 1) {
      setCurrentPage(newPage)
    }
  }

  useEffect(() => {
    // If total number of pages changes and we are exceeding the limit, go to the last page. Very
    // unlikely!
    if (hasPages && currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
  }, [hasPages, currentPage, totalPages])

  // Cursor logic
  const pageInfo = useUnsafeMemo(() => {
    if (pagination.kind !== PAGINATION_KIND.CURSOR || !queryResult.data) {
      return undefined
    }

    const pageInfo = source.resolver.dataSource(queryResult.data)?.pageInfo

    if (pageInfo === undefined) {
      throw new Error('pageInfo not present in the response')
    }

    return pageInfo
  }, [pagination.kind, source.resolver, queryResult.data])

  const actions: PaginationActions = {
    first: {
      disabled: !pageInfo?.hasPreviousPage,
      onExecute: () => setVariables({ first: pagination.itemsPerPage }),
    },
    previous: {
      disabled: !pageInfo?.hasPreviousPage,
      onExecute: () =>
        setVariables({ last: pagination.itemsPerPage, before: pageInfo?.startCursor }),
    },
    next: {
      disabled: !pageInfo?.hasNextPage,
      onExecute: () => setVariables({ first: pagination.itemsPerPage, after: pageInfo?.endCursor }),
    },
    last: {
      disabled: !pageInfo?.hasNextPage,
      onExecute: () => setVariables({ last: pagination.itemsPerPage }),
    },
  }

  const hasPaginationInfo = (totalCount ?? 0) > 0 || !!pageInfo

  return {
    pagination,
    offsetPagination: {
      totalCount,
      totalPages,
      goToPage,
      hasPages,
      currentPage,
    },
    cursorPagination: {
      actions,
    },
    hasPaginationInfo,
  }
}
